import { signalPresentation, type SignalItem } from '@indipia/shared'
import { Badge, Skeleton, SignalDot } from '@indipia/ui-core'
import { isSignalColor, SIGNAL_COLOR_LABEL, signalCtx, type SignalTaskContext } from '../../constants/signalView'
import { ORDER_MESSAGE } from '../../constants/materialOrder.logic'

// 발주 신호 한 줄 — 신호 점 + 이름 + 문구. 점·사유의 원천은 서버 ORDER 슬롯 신호뿐이며 폴백을 두지 않는다
// (조회 실패·이 공종의 신호 없음이면 점 대신 오류 표시, PartnerCard 선례).

/** 발주 슬롯 신호 상태 — ok 는 서버 ORDER 슬롯 1줄 */
export type SignalState =
  | { state: 'loading' }
  | { state: 'error' }
  | { state: 'ok'; item: SignalItem }

interface Props {
  signal: SignalState
  /** 문구 조립에 쓰는 공종 값(기간·거래처명 등) */
  taskCtx: SignalTaskContext
  /** 신호 옆에 보조로 덧붙일 서버 발주 상태 이름(신호를 대신하지 않음) */
  fallbackStatus?: string
}

export function OrderStatusLine({ signal, taskCtx, fallbackStatus }: Props) {
  if (signal.state === 'loading') {
    return <div className="flex items-center gap-1.5" aria-busy="true"><Skeleton variant="text" className="w-24" /></div>
  }
  if (signal.state === 'error') {
    return (
      <div className="flex items-center gap-1.5 text-xs" role="alert">
        <span
          role="img"
          aria-label="신호 오류"
          className="inline-flex items-center justify-center rounded-full font-bold w-4 h-4 text-xs leading-none"
          style={{ background: 'var(--status-danger-600)', color: 'var(--shell-surface)' }}
        >!</span>
        <span style={{ color: 'var(--status-danger-600)' }}>{ORDER_MESSAGE.signalError}</span>
      </div>
    )
  }

  const item = signal.item
  const pres = signalPresentation(item.reasonCode, signalCtx(item, taskCtx))
  return (
    <div className="flex items-center gap-1.5 min-w-0" aria-live="polite">
      {isSignalColor(item.colorCode) && (
        <SignalDot color={item.colorCode} label={`발주 신호 ${SIGNAL_COLOR_LABEL[item.colorCode]}`} />
      )}
      {pres ? (
        <>
          <b className="text-xs shrink-0" style={{ color: 'var(--shell-on-surface)' }}>{pres.name}</b>
          <span className="text-xs min-w-0 truncate" style={{ color: 'var(--shell-on-surface-2)' }}>{pres.line}</span>
        </>
      ) : (
        <span className="text-xs" style={{ color: 'var(--status-danger-600)' }}>{ORDER_MESSAGE.signalUnknown}</span>
      )}
      {fallbackStatus && <Badge status="mute">{fallbackStatus}</Badge>}
    </div>
  )
}
